import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react'

import RecaptchaManager, { RecaptchaCustomAction } from './manager'

interface RecaptchaContextProps {
  executeRecaptcha: RecaptchaManager['execute']
  isLoaded: boolean
  loadRecaptcha: () => Promise<void>
  isError: boolean
}

const RecaptchaContext = createContext<RecaptchaContextProps | null>(null)

export const RecaptchaProvider = ({ children }: { children: React.ReactNode }) => {
  const manager = useMemo(() => RecaptchaManager.getInstance(), [])
  const [isLoaded, setIsLoaded] = useState(false)
  const [isError, setIsError] = useState(false)

  const loadRecaptcha = useCallback(async () => {
    try {
      await manager.ensureLoaded()
      setIsLoaded(true)
    } catch (error) {
      setIsError(true)
    }
  }, [manager])

  const executeRecaptcha = useCallback(
    async (action: RecaptchaCustomAction) => {
      const recaptchaToken = await manager.execute(action)
      return recaptchaToken
    },
    [manager],
  )

  return (
    <RecaptchaContext.Provider value={{ isLoaded, loadRecaptcha, executeRecaptcha, isError }}>
      {children}
    </RecaptchaContext.Provider>
  )
}

export const useRecaptchaPage = (): RecaptchaContextProps => {
  const context = useContext(RecaptchaContext)
  if (!context) {
    throw new Error('useRecaptchaPage must be used within a RecaptchaProvider')
  }
  const { loadRecaptcha, executeRecaptcha, isLoaded, isError } = context

  useEffect(() => {
    loadRecaptcha()
  }, [loadRecaptcha])

  return { executeRecaptcha, isLoaded, loadRecaptcha, isError }
}
